/**
 * Credit-adjudicator-facing labels for the PDL Decision Engine tab.
 * Keep wording plain — audit/trace codes stay in collapsed technical sections.
 */

/** ApplicationSectionCard subtitle (Decision Engine panel header). */
export const PDL_PANEL_SUBTITLE =
  'Payday loan capacity from verified bank income · no credit bureau pull';

/** Brief card — primary title inside the glass summary. */
export const PDL_BRIEF_TITLE = 'Offer summary';

/** Brief card — one line under the title. */
export const PDL_BRIEF_SUBTITLE =
  'Company pre-approval table caps the offer · bank affordability on expand';

/** Guided waterfall — section title. */
export const PDL_WATERFALL_TITLE = 'What capped the approved amount';

/** Full-breakdown dialog title. */
export const PDL_BREAKDOWN_DIALOG_TITLE = 'Capacity calculation breakdown';

/** Full-breakdown dialog subtitle. */
export const PDL_BREAKDOWN_DIALOG_SUBTITLE =
  'Legacy Cash4You scoring table — how table, band, and tier produce the pre-approval limit.';

export const PDL_LEGACY_CALC_COPY = {
  title: 'Pre-approval table calculation',
  subtitle: 'How table, band, and tier produce the company limit (legacy Cash4You grid).',
  profileTitle: 'Scoring profile',
} as const;

export const PDL_AMOUNT_STRIP = {
  requested: 'Customer requested',
  approved: 'Approved offer',
} as const;

export const PDL_EVIDENCE_COPY = {
  sectionTitle: 'Income & debt evidence',
  incomeTitle: 'Bank income streams',
  debtTitle: 'Existing debts (ELOD)',
  debtHint:
    'Loan payments we found leaving the customer’s bank account (ELOD = existing loan obligation detection). These reduce how much more they can afford to borrow.',
  incomeHint: 'Regular income we found being paid into the customer’s connected bank account, converted to a monthly amount.',
  existingPayments: 'Monthly loan payments',
  elodStatus: 'ELOD bank feed',
  obligationsTotal: 'Total',
  openIncomeTab: 'Full income verification →',
} as const;

export const PDL_WATERFALL_LAYER = {
  observation: {
    label: 'Customer request',
    hint: 'What they asked to borrow',
  },
  policy: {
    label: 'Pre-approval limit',
    hint: 'Cash4You scoring table for this profile',
  },
  policyCompare: {
    label: 'Limits compared',
    hint: 'We take the lower of these two ceilings',
  },
  decision: {
    label: 'Offer to customer',
    hint: 'Final amount after all limits',
  },
} as const;

export const PDL_WATERFALL_BAR = {
  requested: {
    label: 'Amount requested',
    sublabel: 'On the application',
  },
  table: {
    label: 'Company pre-approval limit',
    sublabel: 'Cash4You scoring table (income profile + loan history)',
  },
  bank: {
    label: 'Bank affordability limit',
    sublabel: 'Max principal from payment room (40% income / 50% total debt)',
  },
  final: {
    label: 'Approved amount',
    sublabel: 'What we can offer today',
  },
} as const;

export const PDL_BINDING = {
  chip: 'Limiting factor',
  headlineLegacy: 'Company pre-approval table set the cap',
  headlineAffordability: 'Bank affordability set the cap',
  headlineRequest: 'Customer request set the cap',
  detailLegacy: (table: string, bank: string) =>
    `Pre-approval limit ${table} is lower than bank affordability ${bank}.`,
  detailLegacyShort: (table: string) => `Pre-approval limit ${table} set the approved amount.`,
  detailAffordability: (bank: string, table: string) =>
    `Bank affordability ${bank} is lower than pre-approval limit ${table}.`,
} as const;

export const PDL_WATERFALL_BANK_TOGGLE = {
  show: 'Compare bank affordability limit',
  hide: 'Hide bank affordability limit',
} as const;

/** Scoring profile chips (compact). */
export function formatPdlScoringChip(kind: 'table' | 'band' | 'tier', raw: string): string {
  if (kind === 'table') {
    const n = raw.match(/(\d+)/);
    return n ? `Scoring table ${n[1]}` : raw;
  }
  if (kind === 'band') {
    const cleaned = raw.replace(/^Band\s*/i, '').replace(/\/mo$/i, '/month').trim();
    if (/^over/i.test(cleaned)) {
      return `Income ${cleaned.charAt(0).toLowerCase()}${cleaned.slice(1)}`;
    }
    return cleaned ? `Income band · ${cleaned}` : 'Income band';
  }
  if (kind === 'tier') {
    if (/first-time|no prior/i.test(raw)) return 'First-time customer';
    return raw.replace(/^Tier\s*/i, 'Repeat tier · ');
  }
  return raw;
}
